import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { PURGE } from "redux-persist";
import apiInstance from '../../../../api';
import ym from 'react-yandex-metrika';

export const getOrders = createAsyncThunk(
  'kitchenOrders/getOrders',
  async (data, { rejectWithValue, dispatch }) => {
    const api = '/documents/checklist/getOrders?XDEBUG_SESSION_START=MyVSCode';

    try {
      const response = await apiInstance.post(api, JSON.stringify({ data }));

      if (response.data.result) {
        const responseDataOrders = response.data.data.orders || response.data.data;
        const responseDataCanceledOrders = response.data.data.canceledOrders || response.data.data;

        const modifyOrders = responseDataOrders.map(order => {
          const modifiedProducts = order.products.map(product => {
            const productReviews = order.reviews.filter(review => review.productId === product.productId);
            if (productReviews.length > 0) {
              const firstReview = productReviews[0];
              return {
                ...product,
                rating: firstReview.rating,
                comment: firstReview.comment,
              };
            } else {
                return product;
            }
          });

          return {
            ...order,
            products: modifiedProducts,
          };
        });

        return {
          orders: modifyOrders,
          canceledOrders: responseDataCanceledOrders
        };
      } else {
        dispatch(clearOrders());
        return rejectWithValue('Failed to fetch data');
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
    }
  }
);

export const generationLink = createAsyncThunk(
  'kitchenOrders/generationLink',
  async (data, { rejectWithValue }) => {
    const api = '/documents/checklist/generationLink?XDEBUG_SESSION_START=MyVSCode';

    try {
      const response = await apiInstance.post(api, JSON.stringify({ data }));

      if (response.data.result) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to fetch data');
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
    }
  }
);

export const getOrdersByAccessToken = createAsyncThunk(
  'kitchenOrders/getOrdersByAccessToken',
  async (data, { rejectWithValue }) => {
    const api = '/documents/checklist/getOrdersByAccessToken?XDEBUG_SESSION_START=MyVSCode';

    try {
      const response = await apiInstance.post(api, JSON.stringify({ data }));

      if (response.data.result) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to fetch data');
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
    }
  }
);

export const markOrdersAsPrinted = createAsyncThunk(
  'kitchenOrders/markOrdersAsPrinted',
  async (data, { rejectWithValue }) => {
    const api = '/documents/checklist/markOrdersAsPrinted?XDEBUG_SESSION_START=MyVSCode';

    try {
      const response = await apiInstance.post(api, JSON.stringify({ data }));

      if (response.data.result) {
        ym('reachGoal','checklist_print');
        return response.data.data;
      } else {
        return rejectWithValue('Failed to fetch data');
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
    }
  }
);

// Мемоизированный селектор для фильтрации заказов по статусу и поисковому запросу
const selectKitchenOrders = (state) => state.kitchenOrders.orders;
const selectKitchenCanceledOrders = (state) => state.kitchenOrders.canceledOrders;
const selectSearchQuery = (state) => state.kitchenOrders.searchQuery;
const selectSearchStatus = (state) => state.kitchenOrders.searchStatus;

export const selectFilteredOrders = createSelector(
  [selectKitchenOrders, selectKitchenCanceledOrders, selectSearchQuery, selectSearchStatus],
  (orders, canceledOrders, searchQuery, searchStatus) => {
    // Выбираем массив для фильтрации в зависимости от статуса
    const source = searchStatus === 'canceled' ? canceledOrders : orders;
    console.log('Source orders for filtering:', source, "canceledOrders",canceledOrders, 'orders', orders , 'searchStatus', searchStatus  );
    let filtered = source.filter(order => {
      switch (searchStatus) {
        case 'new':
          return order.status !== 100 && !order.isPrinted;
        case 'printed':
          return order.isPrinted;
        case 'canceled':
          return order.status === 100 && order.isPrinted;
        default:
          return order.status !== 100 && !order.isPrinted;
      }
    });

    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();

      filtered = filtered.filter(order => {
        const idStr = order.id !== undefined && order.id !== null ? String(order.id).toLowerCase() : '';
        const locationIdStr = order.locationId !== undefined && order.locationId !== null ? String(order.locationId).toLowerCase() : '';
        const addressIdStr = order.addressId !== undefined && order.addressId !== null ? String(order.addressId).toLowerCase() : '';

        return (
          idStr.includes(q) ||
          locationIdStr.includes(q) ||
          addressIdStr.includes(q)
        );
      });
    }

    return filtered;
  }
);

const initialState = {
  selectedDate: null,
  selectedRace: null,
  selectedPolygons: [],

  // Показывать таблицу сканированных заказов вместо основного представления
  isScansTableOpen: false,

  ordersGetRequestStatus: null,
  orders: [],
  canceledOrders: [],
  searchQuery: '',
  searchStatus: 'new', // 'new', 'printed', 'canceled'
  selectedOrders: [],
};

const kitchenOrdersSlice = createSlice({
  name: 'kitchenOrders',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(getOrders.pending, (state) => { state.ordersGetRequestStatus = 'pending'; })
      .addCase(getOrders.fulfilled, (state, action) => { state.ordersGetRequestStatus = 'fulfilled'; state.orders = action.payload.orders; state.canceledOrders = action.payload.canceledOrders; })
      .addCase(getOrders.rejected, (state) => { state.ordersGetRequestStatus = 'rejected'; })

      .addCase(PURGE, () => { return initialState; });
  },
  reducers: {
    setSelectedDate: (state, action) => { state.selectedDate = action.payload; },
    setScansTableOpen: (state, action) => { state.isScansTableOpen = !!action.payload; },
    setSelectedRace: (state, action) => {
      const prevRace = state.selectedRace;
      state.selectedRace = action.payload;
      
      // Если рейс не изменился, не обновляем полигоны
      if (prevRace?.raceId === action.payload?.raceId) {
        return;
      }
      
      // Обновляем полигоны только если рейс действительно изменился
      const newPolygons = action.payload?.periods?.flatMap(period => period.kitchenRacePolygons) || [];
      
      // Проверяем, изменились ли полигоны по содержимому
      const currentIds = state.selectedPolygons.map(p => p.kitchenRacePolygonId).sort().join(',');
      const newIds = newPolygons.map(p => p.kitchenRacePolygonId).sort().join(',');
      
      if (currentIds !== newIds) {
        state.selectedPolygons = newPolygons;
      }
    },
    updateSelectedPolygons: (state, action) => {
      const polygon = action.payload;
      if (state.selectedPolygons.some(p => p.kitchenRacePolygonId === polygon.kitchenRacePolygonId)) {
        state.selectedPolygons = state.selectedPolygons.filter(p => p.kitchenRacePolygonId !== polygon.kitchenRacePolygonId);
      } else {
        state.selectedPolygons = [...state.selectedPolygons, polygon];
      }
    },
    setSearchQuery: (state, action) => { state.searchQuery = action.payload; },
    setSearchStatus: (state, action) => { state.searchStatus = action.payload; },
    setSelectedOrder: (state, action) => { state.selectedOrders = action.payload; },
    clearOrders: (state) => { state.ordersGetRequestStatus = null; state.orders = []; },
  },
});

export const {
  setSelectedDate,
  setSelectedRace,
  updateSelectedPolygons,
  setSearchQuery,
  setSearchStatus,
  setSelectedOrder,
  setScansTableOpen,
  clearOrders,
} = kitchenOrdersSlice.actions;

export default kitchenOrdersSlice.reducer;
